import { BreakpointObserver } from '@angular/cdk/layout';
import { Component, Inject, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatBadgeModule } from '@angular/material/badge';

import { LanguageService } from 'app/core/i18n/language.service';
import { TranslatePipe } from 'app/core/i18n/translate.pipe';

import { DocumentItem } from '../../interfaces/document-item.interface';
import { DocumentPreviewData, LineAltUnits } from '../../interfaces/dialog-data.interface';
import { DgNumberPipe } from '../../dg-number.pipe';
import { altUnitsOf } from '../../alt-units.util';
import { DocumentGenerateConfigService } from '../../document-generate-config.service';
import { BomCellComponent } from '../../components/bom-cell/bom-cell.component';
import { ItemDetailModalComponent } from '../item-detail-modal/item-detail-modal.component';
import { DocumentSplitModalComponent } from '../document-split-modal/document-split-modal.component';
import { WarehouseDistributionModalComponent } from '../warehouse-distribution-modal/warehouse-distribution-modal.component';

@Component({
  selector: 'app-document-preview-modal',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatBadgeModule,
    DgNumberPipe,
    BomCellComponent,
    TranslatePipe,
  ],
  templateUrl: './document-preview-modal.component.html',
})
export class DocumentPreviewModalComponent implements OnInit, OnDestroy {
  private openedUrls: string[] = [];

  // en pantallas de poca altura (móvil horizontal) el encabezado no deja ver los artículos
  shortScreen = false;
  private shortScreenSub?: Subscription;

  private readonly i18n = inject(LanguageService);

  constructor(
    private dialogRef: MatDialogRef<DocumentPreviewModalComponent>,
    private dialog: MatDialog,
    public dgConfig: DocumentGenerateConfigService,
    private breakpointObserver: BreakpointObserver,
    @Inject(MAT_DIALOG_DATA) public data: DocumentPreviewData
  ) {}

  ngOnInit(): void {
    this.shortScreenSub = this.breakpointObserver
      .observe('(max-height: 560px)')
      .subscribe((state) => (this.shortScreen = state.matches));
  }

  // la configuración de la clase produjo más de un documento
  get isSplit(): boolean {
    return this.documentCount > 1;
  }

  get documentCount(): number {
    return this.data.documents?.length ?? 0;
  }

  // el aviso depende de las banderas de la clase, no de que la partición llegue a ocurrir
  get showsSplitNotice(): boolean {
    return this.data.splitsByWarehouse === true || this.data.separatesWithoutStock === true;
  }

  get documentClassLabel(): string {
    const code = this.data.documentClassCode ?? '';
    const name = this.data.documentClass ?? '';
    return code && name ? `${code} - ${name}` : code || name;
  }

  // qué hace la clase según las banderas activas
  get splitNoticeConfig(): string {
    const byWarehouse = this.data.splitsByWarehouse === true;
    const withoutStock = this.data.separatesWithoutStock === true;
    if (byWarehouse && withoutStock) {
      return this.i18n.t('DOCUMENT_GENERATE.PREVIEW_MODAL.CONFIG_BOTH');
    }
    if (byWarehouse) return this.i18n.t('DOCUMENT_GENERATE.PREVIEW_MODAL.CONFIG_BY_WAREHOUSE');
    return this.i18n.t('DOCUMENT_GENERATE.PREVIEW_MODAL.CONFIG_WITHOUT_STOCK');
  }

  // qué produce el contenido actual; con un solo documento se explica por qué no se partió
  get splitNoticeResult(): string {
    if (this.isSplit) {
      return this.i18n.t('DOCUMENT_GENERATE.PREVIEW_MODAL.RESULT_SPLIT', { count: this.documentCount });
    }
    if (this.data.splitsByWarehouse === true) {
      return this.i18n.t('DOCUMENT_GENERATE.PREVIEW_MODAL.RESULT_SINGLE_SAME_WAREHOUSE');
    }
    return this.i18n.t('DOCUMENT_GENERATE.PREVIEW_MODAL.RESULT_SINGLE_HAS_STOCK');
  }

  warehouseLabel(item: DocumentItem): string {
    const code = item.warehouseCode ?? '';
    const name = item.warehouseName ?? '';
    return code && name ? `${code} - ${name}` : code || name;
  }

  // la línea se abastece de varios almacenes: el reparto vive en sus asignaciones
  allocationCount(item: DocumentItem): number {
    const allocations = item.allocations ?? [];
    return allocations.length > 1 ? allocations.length : 0;
  }

  // reparto de la línea en consulta: la vista previa no permite modificarlo
  openAllocations(item: DocumentItem): void {
    this.dialog.open(WarehouseDistributionModalComponent, {
      data: {
        productLabel: `${item.code} - ${item.name}`,
        unitLabel: item.unitLabel,
        requested: item.quantity ?? 0,
        warehouses: [],
        allocations: item.allocations ?? [],
        readonly: true,
        // en consulta el reparto ya viene hecho, pero el texto debe mostrar sus decimales
        acceptsDecimals: item.hasAcceptedDecimals !== false,
        decimalsAllowed: item.decimalsAllowed,
      },
      ...this.dgConfig.dialogSize('md'),
    });
  }

  openSplit(): void {
    this.dialog.open(DocumentSplitModalComponent, {
      data: {
        documents: this.data.documents ?? [],
        currency: this.data.currency,
        taxIncluded: this.data.taxIncluded,
        onChangeRoute: this.data.onChangeRoute,
      },
      ...this.dgConfig.dialogSize('lg'),
    });
  }

  // contador de notas + adjuntos por línea (subidos y nuevos)
  detailCount(item: DocumentItem): number {
    return (item.note ? 1 : 0) + (item.evidences?.length ?? 0) + (item.newAttachments?.length ?? 0);
  }

  // abre el detalle de artículo en solo lectura (con stock si la clase lo permite)
  openDetail(item: DocumentItem): void {
    this.dialog.open(ItemDetailModalComponent, {
      data: {
        item,
        readonly: true,
        showStock: false,
      },
      ...this.dgConfig.dialogSize('md'),
    });
  }

  ngOnDestroy(): void {
    this.shortScreenSub?.unsubscribe();
    this.openedUrls.forEach((url) => URL.revokeObjectURL(url));
    this.openedUrls = [];
  }

  // el visor del navegador resuelve pdf e imágenes
  openAttachment(file: File): void {
    const url = URL.createObjectURL(file);
    this.openedUrls.push(url);
    window.open(url, '_blank');
  }

  attachmentIcon(file: File): string {
    return file.type === 'application/pdf' ? 'picture_as_pdf' : 'image';
  }

  formatFileSize(bytes: number): string {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  }

  taxLabel(item: DocumentItem): string {
    return item.taxName || (item.taxPct ? item.taxPct + '%' : '-');
  }

  // conversión de la cantidad de la línea a unidad secundaria y paralela
  altUnits(item: DocumentItem): LineAltUnits {
    return altUnitsOf(item.material, item.unitMeasureId, item.quantity, this.data.umpActive !== false);
  }

  rowSubtotal(item: DocumentItem): number {
    return item.quantity * item.price - (this.data.taxIncluded ? item.isv : 0);
  }

  rowTotal(item: DocumentItem): number {
    return this.rowSubtotal(item) - item.discount + item.isv;
  }

  cancel(): void {
    this.dialogRef.close(false);
  }

  confirm(): void {
    this.dialogRef.close(true);
  }

  // genera y abre inmediatamente un nuevo documento con el mismo cliente
  confirmSameClient(): void {
    this.dialogRef.close('sameClient');
  }
}
